"use client";

import { animate, useInView, useReducedMotion } from "motion/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface AnimatedNumberProps {
  value: number;
  className?: string;
  duration?: number;
  format?: (value: number) => string;
  initialValue?: number;
  startOnView?: boolean;
}

const defaultFormat = (value: number): string => Math.round(value).toString();

export function AnimatedNumber({
  value,
  className,
  duration = 0.55,
  format = defaultFormat,
  initialValue,
  startOnView = true,
}: AnimatedNumberProps): React.ReactElement {
  const sharedProps = {
    value,
    className,
    duration,
    format,
    initialValue: initialValue ?? (startOnView ? 0 : value),
  };

  return startOnView ? (
    <InViewAnimatedNumber {...sharedProps} />
  ) : (
    <AnimatedNumberValue {...sharedProps} active />
  );
}

function InViewAnimatedNumber(props: Omit<AnimatedNumberProps, "startOnView">): React.ReactElement {
  const elementRef = useRef<HTMLSpanElement>(null);
  const isInView = useInView(elementRef, { once: true });
  return <AnimatedNumberValue {...props} active={isInView} elementRef={elementRef} />;
}

function AnimatedNumberValue({
  value,
  className,
  duration = 0.55,
  format = defaultFormat,
  initialValue = value,
  active,
  elementRef,
}: Omit<AnimatedNumberProps, "startOnView"> & {
  active: boolean;
  elementRef?: React.RefObject<HTMLSpanElement>;
}): React.ReactElement {
  const fallbackRef = useRef<HTMLSpanElement>(null);
  const formatterRef = useRef(format);
  const displayValueRef = useRef(initialValue);
  const [displayValue, setDisplayValue] = useState(initialValue);
  const prefersReducedMotion = useReducedMotion();
  formatterRef.current = format;

  useEffect(() => {
    if (!active) return;

    const from = displayValueRef.current;
    if (prefersReducedMotion || duration === 0 || from === value) {
      displayValueRef.current = value;
      setDisplayValue(value);
      return;
    }

    const controls = animate(from, value, {
      duration,
      ease: "easeOut",
      onUpdate: (nextValue) => {
        displayValueRef.current = nextValue;
        setDisplayValue(nextValue);
      },
    });
    return () => controls.stop();
  }, [active, duration, prefersReducedMotion, value]);

  return (
    <span
      ref={elementRef ?? fallbackRef}
      className={cn("tabular-nums", className)}
      data-slot="animated-number"
    >
      <span aria-hidden data-slot="animated-number-display">
        {formatterRef.current(displayValue)}
      </span>
      <span className="sr-only" data-slot="animated-number-label">
        {formatterRef.current(value)}
      </span>
    </span>
  );
}
